import { useState } from 'react';
import { Download, ListPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import PriorityCard from './PriorityCard';
import MarkdownText from '../MarkdownText';
import { getSection, formatPeriod } from '../utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Onglet "Plan d'action" — Section V du rapport cabinet.
 *
 * Trois priorités structurées (PRIORITÉ 01/02/03) avec question de contexte,
 * analyse senior chiffrée, plan en 5 étapes datées, footer impact/difficulté/délai.
 *
 * Boutons en pied :
 *   - "Télécharger le rapport en PDF" (placeholder, viendra à l'étape 7)
 *   - "Ajouter ces 3 priorités à mon plan d'action" — POST bulk vers le
 *     backend, dédup automatique sur (user, source_module, source_id).
 *
 * Dégradation gracieuse :
 *   - content_data.priorities[] présent → 3 PriorityCards + boutons actifs
 *   - content_md seulement → rendu markdown brut + boutons désactivés
 *   - rien → placeholder élégant
 */
export default function ActionPlan({ report }) {
  const section = getSection(report, 'action_plan');
  const data = section?.content_data || {};
  const priorities = Array.isArray(data.priorities) ? data.priorities.slice(0, 3) : [];
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);  // {created, duplicates, total}

  const periodReadable = formatPeriod(report?.period);
  const hasStructuredPriorities = priorities.length > 0;
  const importDisabled = !hasStructuredPriorities || importing || importResult?.created > 0;

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (!report?.id || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('benchory_token');
      const res = await fetch(`${API_URL}/client/monthly-reports/${report.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error('Le téléchargement du PDF a échoué.');
        setDownloadingPdf(false);
        return;
      }
      const blob = await res.blob();
      const filename = `benchory-cabinet-${report.code_officine || report.period}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Le téléchargement du PDF a échoué.');
    }
    setDownloadingPdf(false);
  };

  const handleImport = async () => {
    if (!report?.id || importing) return;
    setImporting(true);
    try {
      const token = localStorage.getItem('benchory_token');
      const res = await fetch(`${API_URL}/client/monthly-reports/${report.id}/import-priorities`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("L'import a échoué. Veuillez réessayer dans un instant.");
        setImporting(false);
        return;
      }
      const result = await res.json();
      setImportResult(result);
      const { created, duplicates } = result || { created: 0, duplicates: 0 };
      if (created === 0 && duplicates > 0) {
        toast(`Ces priorités sont déjà dans votre plan d'action.`);
      } else if (created > 0 && duplicates === 0) {
        toast.success(
          created === 1 ? `1 priorité ajoutée à votre plan d'action.`
            : `${created} priorités ajoutées à votre plan d'action.`,
        );
      } else if (created > 0 && duplicates > 0) {
        toast.success(`${created} priorité${created > 1 ? 's' : ''} ajoutée${created > 1 ? 's' : ''} · ${duplicates} déjà dans votre plan.`);
      } else {
        toast("Aucune priorité à importer pour le moment.");
      }
    } catch (err) {
      toast.error("L'import a échoué. Veuillez réessayer dans un instant.");
    }
    setImporting(false);
  };

  return (
    <section data-testid="action-plan-tab">
      <Toaster />

      <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.3em] font-medium mb-2">
        V — Plan d'action
      </div>
      <h2 className="font-playfair text-[22px] text-cabinet-ink mb-3">
        Vos points de vigilance et le plan d'action de {periodReadable}
      </h2>
      <p className="font-playfair italic text-[14px] text-cabinet-ink-secondary mb-10 max-w-2xl">
        Trois priorités identifiées par notre équipe d'analyse, avec contexte, recommandation et étapes concrètes.
      </p>

      {hasStructuredPriorities ? (
        <div className="space-y-6 mb-12">
          {priorities.map((priority, i) => (
            <PriorityCard
              key={i}
              priority={priority}
              rank={priority.rank || i + 1}
              report={report}
            />
          ))}
        </div>
      ) : section?.content_md ? (
        <div className="bg-cabinet-paper border border-cabinet-border shadow-sm p-7 mb-12">
          <MarkdownText
            text={section.content_md}
            className="text-[14px] text-cabinet-ink leading-[1.75]"
          />
        </div>
      ) : (
        <div className="bg-cabinet-paper border border-cabinet-border shadow-sm p-12 text-center mb-12">
          <p className="font-playfair italic text-[14px] text-cabinet-ink-soft leading-[1.85]">
            Le plan d'action sera disponible dès la finalisation du prompt système maître et la génération complète du rapport.
          </p>
        </div>
      )}

      {/* Boutons */}
      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] font-medium uppercase tracking-[0.2em] transition ${
            downloadingPdf
              ? 'bg-cabinet-bordeaux/50 text-cabinet-cream cursor-wait'
              : 'bg-cabinet-bordeaux text-cabinet-cream hover:bg-cabinet-bordeaux-dark'
          }`}
          data-testid="download-pdf-btn"
        >
          <Download size={14} /> {downloadingPdf ? 'Préparation du PDF…' : 'Télécharger le rapport en PDF'}
        </button>

        <button
          type="button"
          onClick={handleImport}
          disabled={importDisabled}
          className={`inline-flex items-center gap-2 px-5 py-3 border-2 text-[11px] font-medium uppercase tracking-[0.2em] transition ${
            importDisabled
              ? 'border-cabinet-border text-cabinet-ink-soft cursor-not-allowed'
              : 'border-cabinet-bordeaux text-cabinet-bordeaux hover:bg-cabinet-bordeaux hover:text-cabinet-cream'
          }`}
          data-testid="import-priorities-btn"
        >
          {importResult?.created > 0
            ? <><Check size={14} /> Ajoutées à votre plan</>
            : importing
              ? <>Importation…</>
              : <><ListPlus size={14} /> Ajouter ces priorités à mon plan d'action</>}
        </button>
      </div>

      {!hasStructuredPriorities && (
        <p className="text-[11px] text-cabinet-ink-soft italic mt-3">
          L'ajout au plan d'action sera actif dès que les priorités structurées seront produites par le cabinet.
        </p>
      )}
    </section>
  );
}
